/**
 * Penanggalan Tionghoa (Chinese Lunar Calendar) + Shio (Zodiak Cina).
 *
 * Algoritma: tabel lunar info per tahun (1900-2100), tiap entry adalah
 * bitmask yang menyimpan info bulan kabisat dan panjang tiap bulan
 * (29/30 hari). Diadaptasi dari implementasi public domain (Hong Kong
 * Observatory style).
 *
 * Output:
 *  - lunarYear / lunarMonth / lunarDay / isLeapMonth
 *  - cyclicalYear (gabungan Stem 10 + Branch 12 = 60-tahunan)
 *  - zodiac (12 shio: Tikus..Babi)
 *  - element (5 elemen: Kayu/Api/Tanah/Logam/Air) + yinYang
 *  - festival utama (Imlek, Cap Go Meh, Qingming, Duanwu, Zhongqiu)
 */

// Lunar info tahun 1900..2100 (compressed). Sumber tabel publik.
// Setiap nilai 16-bit: bit 0..3 = bulan kabisat (0 = tidak ada),
// bit 4..15 = panjang 12 bulan reguler dari atas (1=30 hari, 0=29 hari),
// dan bit terpisah untuk panjang bulan kabisat (high bit).
const LUNAR_INFO: number[] = [
  0x04bd8, 0x04ae0, 0x0a570, 0x054d5, 0x0d260, 0x0d950, 0x16554, 0x056a0, 0x09ad0, 0x055d2, // 1900-1909
  0x04ae0, 0x0a5b6, 0x0a4d0, 0x0d250, 0x1d255, 0x0b540, 0x0d6a0, 0x0ada2, 0x095b0, 0x14977, // 1910-1919
  0x04970, 0x0a4b0, 0x0b4b5, 0x06a50, 0x06d40, 0x1ab54, 0x02b60, 0x09570, 0x052f2, 0x04970, // 1920-1929
  0x06566, 0x0d4a0, 0x0ea50, 0x06e95, 0x05ad0, 0x02b60, 0x186e3, 0x092e0, 0x1c8d7, 0x0c950, // 1930-1939
  0x0d4a0, 0x1d8a6, 0x0b550, 0x056a0, 0x1a5b4, 0x025d0, 0x092d0, 0x0d2b2, 0x0a950, 0x0b557, // 1940-1949
  0x06ca0, 0x0b550, 0x15355, 0x04da0, 0x0a5b0, 0x14573, 0x052b0, 0x0a9a8, 0x0e950, 0x06aa0, // 1950-1959
  0x0aea6, 0x0ab50, 0x04b60, 0x0aae4, 0x0a570, 0x05260, 0x0f263, 0x0d950, 0x05b57, 0x056a0, // 1960-1969
  0x096d0, 0x04dd5, 0x04ad0, 0x0a4d0, 0x0d4d4, 0x0d250, 0x0d558, 0x0b540, 0x0b6a0, 0x195a6, // 1970-1979
  0x095b0, 0x049b0, 0x0a974, 0x0a4b0, 0x0b27a, 0x06a50, 0x06d40, 0x0af46, 0x0ab60, 0x09570, // 1980-1989
  0x04af5, 0x04970, 0x064b0, 0x074a3, 0x0ea50, 0x06b58, 0x055c0, 0x0ab60, 0x096d5, 0x092e0, // 1990-1999
  0x0c960, 0x0d954, 0x0d4a0, 0x0da50, 0x07552, 0x056a0, 0x0abb7, 0x025d0, 0x092d0, 0x0cab5, // 2000-2009
  0x0a950, 0x0b4a0, 0x0baa4, 0x0ad50, 0x055d9, 0x04ba0, 0x0a5b0, 0x15176, 0x052b0, 0x0a930, // 2010-2019
  0x07954, 0x06aa0, 0x0ad50, 0x05b52, 0x04b60, 0x0a6e6, 0x0a4e0, 0x0d260, 0x0ea65, 0x0d530, // 2020-2029
  0x05aa0, 0x076a3, 0x096d0, 0x04afb, 0x04ad0, 0x0a4d0, 0x1d0b6, 0x0d250, 0x0d520, 0x0dd45, // 2030-2039
  0x0b5a0, 0x056d0, 0x055b2, 0x049b0, 0x0a577, 0x0a4b0, 0x0aa50, 0x1b255, 0x06d20, 0x0ada0, // 2040-2049
  0x14b63, 0x09370, 0x049f8, 0x04970, 0x064b0, 0x168a6, 0x0ea50, 0x06b20, 0x1a6c4, 0x0aae0, // 2050-2059
  0x0a2e0, 0x0d2e3, 0x0c960, 0x0d557, 0x0d4a0, 0x0da50, 0x05d55, 0x056a0, 0x0a6d0, 0x055d4, // 2060-2069
  0x052d0, 0x0a9b8, 0x0a950, 0x0b4a0, 0x0b6a6, 0x0ad50, 0x055a0, 0x0aba4, 0x0a5b0, 0x052b0, // 2070-2079
  0x0b273, 0x06930, 0x07337, 0x06aa0, 0x0ad50, 0x14b55, 0x04b60, 0x0a570, 0x054e4, 0x0d160, // 2080-2089
  0x0e968, 0x0d520, 0x0daa0, 0x16aa6, 0x056d0, 0x04ae0, 0x0a9d4, 0x0a2d0, 0x0d150, 0x0f252, // 2090-2099
  0x0d520, // 2100
];

const ZODIAC_ID = [
  "Tikus", "Kerbau", "Macan", "Kelinci", "Naga", "Ular",
  "Kuda", "Kambing", "Monyet", "Ayam", "Anjing", "Babi",
];
const ZODIAC_EN = [
  "Rat", "Ox", "Tiger", "Rabbit", "Dragon", "Snake",
  "Horse", "Goat", "Monkey", "Rooster", "Dog", "Pig",
];
const HEAVENLY_STEMS = ["Jia", "Yi", "Bing", "Ding", "Wu", "Ji", "Geng", "Xin", "Ren", "Gui"];
const EARTHLY_BRANCHES = ["Zi", "Chou", "Yin", "Mao", "Chen", "Si", "Wu", "Wei", "Shen", "You", "Xu", "Hai"];
const ELEMENTS = ["Kayu", "Kayu", "Api", "Api", "Tanah", "Tanah", "Logam", "Logam", "Air", "Air"];
const YIN_YANG = ["Yang", "Yin", "Yang", "Yin", "Yang", "Yin", "Yang", "Yin", "Yang", "Yin"];

const LUNAR_MONTH_NAMES = [
  "Zhēng", "Èr", "Sān", "Sì", "Wǔ", "Liù",
  "Qī", "Bā", "Jiǔ", "Shí", "Dōng", "Là",
];

export interface ChineseDate {
  lunarYear: number;
  lunarMonth: number; // 1-12
  lunarDay: number; // 1-30
  isLeapMonth: boolean;
  cyclicalYear: string; // e.g. "Yi Si"
  stem: string;
  branch: string;
  zodiac: string; // Indonesian
  zodiacEn: string;
  element: string; // Kayu/Api/Tanah/Logam/Air
  yinYang: string; // Yin/Yang
  monthName: string; // Zhēng, Èr, ...
  formatted: string; // "1 Zhēng 4723 (Yi Si - Ular Api)"
  festivals: string[]; // hari raya Tionghoa
}

function leapMonth(year: number): number {
  return LUNAR_INFO[year - 1900] & 0xf;
}
function leapDays(year: number): number {
  return leapMonth(year) ? (LUNAR_INFO[year - 1900] & 0x10000 ? 30 : 29) : 0;
}
function monthDays(year: number, month: number): number {
  return LUNAR_INFO[year - 1900] & (0x10000 >> month) ? 30 : 29;
}
function yearDays(year: number): number {
  let sum = 348;
  for (let i = 0x8000; i > 0x8; i >>= 1) {
    sum += LUNAR_INFO[year - 1900] & i ? 1 : 0;
  }
  return sum + leapDays(year);
}

/** Convert Gregorian to Chinese lunar date. */
export function gregorianToChinese(year: number, month: number, day: number): ChineseDate {
  const baseDate = Date.UTC(1900, 0, 31); // 1900-01-31 = Lunar 1900-01-01
  const target = Date.UTC(year, month - 1, day);
  let offset = Math.floor((target - baseDate) / 86400000);

  let ly = 1900;
  let temp = 0;
  for (; ly < 2101 && offset > 0; ly++) {
    temp = yearDays(ly);
    offset -= temp;
  }
  if (offset < 0) {
    offset += temp;
    ly--;
  }

  const leap = leapMonth(ly);
  let isLeap = false;
  let lm = 1;
  for (; lm < 13 && offset > 0; lm++) {
    if (leap > 0 && lm === leap + 1 && !isLeap) {
      --lm;
      isLeap = true;
      temp = leapDays(ly);
    } else {
      temp = monthDays(ly, lm);
    }
    if (isLeap && lm === leap + 1) isLeap = false;
    offset -= temp;
  }
  if (offset === 0 && leap > 0 && lm === leap + 1) {
    if (isLeap) {
      isLeap = false;
    } else {
      isLeap = true;
      --lm;
    }
  }
  if (offset < 0) {
    offset += temp;
    --lm;
  }
  const ld = offset + 1;

  // Sexagenary cycle. 1984 = Jia Zi (start of cycle). 1900 = Geng Zi → stem index 6, branch 0.
  const cyc = (ly - 1900 + 36) % 60; // 1900 = index 36 in cycle starting at Jia Zi (1984-60=1924 Jia Zi; alt: 1864).
  const stemIdx = cyc % 10;
  const branchIdx = cyc % 12;
  const stem = HEAVENLY_STEMS[stemIdx];
  const branch = EARTHLY_BRANCHES[branchIdx];
  const zodiac = ZODIAC_ID[branchIdx];
  const zodiacEn = ZODIAC_EN[branchIdx];
  const element = ELEMENTS[stemIdx];
  const yinYang = YIN_YANG[stemIdx];

  const festivals: string[] = [];
  if (lm === 1 && ld === 1) festivals.push("Tahun Baru Imlek");
  if (lm === 1 && ld === 15) festivals.push("Cap Go Meh");
  if (lm === 5 && ld === 5) festivals.push("Duanwu (Festival Perahu Naga)");
  if (lm === 7 && ld === 15) festivals.push("Zhongyuan (Festival Hantu)");
  if (lm === 8 && ld === 15) festivals.push("Zhongqiu (Festival Kue Bulan)");
  if (lm === 9 && ld === 9) festivals.push("Chongyang");
  if (lm === 12 && ld === 8) festivals.push("Laba");
  // Qingming biasanya 4 atau 5 April Gregorian
  if (month === 4 && (day === 4 || day === 5)) festivals.push("Qingming");

  const monthName = (isLeap ? "Rùn " : "") + LUNAR_MONTH_NAMES[lm - 1];
  const formatted = `${ld} ${monthName} ${ly} (${stem} ${branch} - ${zodiac} ${element})`;

  return {
    lunarYear: ly,
    lunarMonth: lm,
    lunarDay: ld,
    isLeapMonth: isLeap,
    cyclicalYear: `${stem} ${branch}`,
    stem,
    branch,
    zodiac,
    zodiacEn,
    element,
    yinYang,
    monthName,
    formatted,
    festivals,
  };
}

/** Get shio (zodiac) from birth year only - quick lookup. */
export function getZodiacForYear(birthYear: number): {
  zodiac: string;
  zodiacEn: string;
  element: string;
  yinYang: string;
  cyclicalYear: string;
  trait: string;
} {
  // Use Lunar New Year approx (Feb) - for quick year-based lookup we use Gregorian year directly.
  const c = gregorianToChinese(birthYear, 6, 1);
  return {
    zodiac: c.zodiac,
    zodiacEn: c.zodiacEn,
    element: c.element,
    yinYang: c.yinYang,
    cyclicalYear: c.cyclicalYear,
    trait: ZODIAC_TRAITS[c.zodiac] ?? "",
  };
}

export const ZODIAC_TRAITS: Record<string, string> = {
  Tikus: "Cerdas, adaptif, ambisius, dan pandai mencari peluang.",
  Kerbau: "Tekun, sabar, pekerja keras, dan dapat dipercaya.",
  Macan: "Berani, percaya diri, karismatik, dan menyukai tantangan.",
  Kelinci: "Lembut, elegan, penuh perhatian, dan diplomatis.",
  Naga: "Energik, berkharisma, ambisius, dan beruntung.",
  Ular: "Bijaksana, intuitif, misterius, dan elegan.",
  Kuda: "Aktif, mandiri, ekspresif, dan menyukai kebebasan.",
  Kambing: "Lembut, kreatif, simpatik, dan suka damai.",
  Monyet: "Cerdik, lincah, suka bersosialisasi, dan inovatif.",
  Ayam: "Teliti, jujur, terorganisir, dan percaya diri.",
  Anjing: "Setia, jujur, bertanggung jawab, dan protektif.",
  Babi: "Murah hati, tulus, optimis, dan mudah berbaur.",
};